import { nanoid } from "nanoid";
import { create } from "zustand";
import type { Question } from "@/types/question";
import { SECTION_TITLE_MAX_LENGTH } from "../types";

export type Section = {
	id: string;
	title: string;
	description?: string;
	questions: Question[];
};

type FormBuilderState = {
	formName: string;
	formDescription: string;
	sections: Section[];
	selectedQuestionIds: number[];
	totalQuestions: number;
	setFormName: (name: string) => void;
	setFormDescription: (desc: string) => void;
	setSelectedQuestionIds: (ids: number[]) => void;
	loadFromTemplate: (payload: {
		formName: string;
		formDescription: string;
		sections: Section[];
	}) => void;
	addSection: () => void;
	updateSectionTitle: (sectionId: string, title: string) => void;
	updateSectionDescription: (sectionId: string, description: string) => void;
	duplicateSection: (sectionId: string) => void;
	deleteSection: (sectionId: string) => void;
	addQuestionsToLastSection: (questions: Question[]) => void;
	deleteQuestion: (questionId: number) => void;
	toggleRequired: (questionId: number, required: boolean) => void;
	moveQuestion: (
		questionId: number,
		fromSectionId: string,
		toSectionId: string,
		targetIndex?: number
	) => void;
	moveSection: (sectionId: string, targetIndex: number) => void;
	getLastSection: () => Section | undefined;
	reset: () => void;
};

const emptyState = {
	formName: "",
	formDescription: "",
	sections: [] as Section[],
	selectedQuestionIds: [] as number[],
	totalQuestions: 0,
};

export const useFormBuilderStore = create<FormBuilderState>((set, get) => ({
	...emptyState,

	setFormName: (name) => set({ formName: name }),
	setFormDescription: (desc) => set({ formDescription: desc }),
	setSelectedQuestionIds: (ids) => set({ selectedQuestionIds: ids }),

	loadFromTemplate: ({ formName, formDescription, sections }) =>
		set({
			formName,
			formDescription,
			sections,
			selectedQuestionIds: sections.flatMap((s) =>
				s.questions.map((q) => q.id)
			),
			totalQuestions: sections.reduce((sum, s) => sum + s.questions.length, 0),
		}),

	addSection: () =>
		set((state) => {
			const newSection: Section = {
				id: nanoid(),
				title: "",
				description: "",
				questions: [],
			};

			return { sections: [...state.sections, newSection] };
		}),

	updateSectionTitle: (sectionId, title) =>
		set((state) => ({
			sections: state.sections.map((s) =>
				s.id === sectionId
					? { ...s, title: title.slice(0, SECTION_TITLE_MAX_LENGTH) }
					: s
			),
		})),

	updateSectionDescription: (sectionId, description) =>
		set((state) => ({
			sections: state.sections.map((s) =>
				s.id === sectionId ? { ...s, description } : s
			),
		})),

	duplicateSection: (sectionId) =>
		set((state) => {
			const section = state.sections.find((s) => s.id === sectionId);
			if (!section) {
				return state;
			}

			const trimmedTitle = section.title.trim();
			let newTitle = "";

			if (trimmedTitle.length > 0) {
				const baseTitle = `${trimmedTitle} (cópia)`.slice(
					0,
					SECTION_TITLE_MAX_LENGTH
				);
				let counter = 1;
				const existingTitles = state.sections.map((s) => s.title.trim());
				newTitle = baseTitle;
				while (existingTitles.includes(newTitle)) {
					counter += 1;
					const suffix = ` ${counter}`;
					newTitle = `${baseTitle.slice(0, SECTION_TITLE_MAX_LENGTH - suffix.length)}${suffix}`;
				}
			}

			const duplicated: Section = {
				id: nanoid(),
				title: newTitle,
				description: section.description,
				questions: section.questions.map((q) => ({ ...q })),
			};

			return {
				sections: [...state.sections, duplicated],
				totalQuestions: state.totalQuestions + duplicated.questions.length,
				selectedQuestionIds: [
					...state.selectedQuestionIds,
					...duplicated.questions.map((q) => q.id),
				],
			};
		}),

	deleteSection: (sectionId) =>
		set((state) => ({
			sections: state.sections.filter((s) => s.id !== sectionId),
			totalQuestions: state.sections
				.filter((s) => s.id !== sectionId)
				.reduce((sum, s) => sum + s.questions.length, 0),
		})),

	addQuestionsToLastSection: (questions) =>
		set((state) => {
			const sections = [...state.sections];
			const last = sections.at(-1);
			if (!last) {
				return state;
			}
			const questionsWithRequired = questions.map((q) => ({
				...q,
				isRequired: q.isRequired ?? false,
			}));
			last.questions.push(...questionsWithRequired);
			return {
				sections,
				totalQuestions: state.totalQuestions + questionsWithRequired.length,
			};
		}),

	deleteQuestion: (questionId) =>
		set((state) => ({
			sections: state.sections.map((s) => ({
				...s,
				questions: s.questions.filter((q) => q.id !== questionId),
			})),
			totalQuestions: state.totalQuestions - 1,
		})),

	toggleRequired: (questionId, required) =>
		set((state) => ({
			sections: state.sections.map((s) => ({
				...s,
				questions: s.questions.map((q) =>
					q.id === questionId ? { ...q, isRequired: required } : q
				),
			})),
		})),

	moveQuestion: (questionId, fromSectionId, toSectionId, targetIndex) => {
		set((state) => {
			const sections = [...state.sections];
			const fromSection = sections.find((s) => s.id === fromSectionId);
			const toSection = sections.find((s) => s.id === toSectionId);
			if (!(fromSection && toSection)) {
				return state;
			}

			const question = fromSection.questions.find((q) => q.id === questionId);
			if (!question) {
				return state;
			}

			fromSection.questions = fromSection.questions.filter(
				(q) => q.id !== questionId
			);

			if (targetIndex !== undefined) {
				toSection.questions.splice(targetIndex, 0, question);
			} else {
				toSection.questions.push(question);
			}

			return { sections };
		});
	},

	moveSection: (sectionId, targetIndex) => {
		set((state) => {
			const fromIndex = state.sections.findIndex((s) => s.id === sectionId);
			if (
				fromIndex === -1 ||
				targetIndex < 0 ||
				targetIndex >= state.sections.length ||
				fromIndex === targetIndex
			) {
				return state;
			}

			const sections = [...state.sections];
			const [section] = sections.splice(fromIndex, 1);
			if (!section) {
				return state;
			}
			// After removal, indices after fromIndex shift left by 1.
			const insertIndex =
				fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
			sections.splice(insertIndex, 0, section);
			return { sections };
		});
	},

	getLastSection: () => {
		const sections = get().sections;
		return sections.at(-1);
	},

	reset: () => set({ ...emptyState }),
}));
